import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
  Fade,
  Collapse
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const projectsData = [
  { title: "Superior Drinks Pvt. Ltd.", loc: "Nagpur, MH", cat: "Consulting", desc: "SVA & SWPP Assessment" },
  { title: "HYMA Developers", loc: "Hyderabad, TS", cat: "Environment", desc: "Environmental Management Plan (WMP)" },
  { title: "Enrich Agro Food Products", loc: "Rohtak, HR", cat: "Consulting", desc: "SVA & SWPP" },
  { title: "Bengal Beverages", loc: "Dankuni, WB", cat: "Audit", desc: "RWH Audit & Gap Assessment" },
  { title: "Global Acqua Pvt. Ltd.", loc: "Howrah, WB", cat: "Audit", desc: "AWS Gap Assessment" },
  { title: "South India Bottling", loc: "Tirunelveli, TN", cat: "Consulting", desc: "SVA & SWPP" },
  { title: "Brindavan Bottlers", loc: "Barabanki, UP", cat: "Consulting", desc: "SVA & SWPP & ESDD" },
  { title: "Bottlers Nepal (Terai)", loc: "Nepal", cat: "Audit", desc: "SVA & WMP, AWS Gap Assessment" },
  { title: "Hindustan Coca-Cola", loc: "Guntur, AP", cat: "Consulting", desc: "SVA & SWPP Execution" },
  { title: "Coca-Cola Beverages", loc: "Sri Lanka", cat: "Audit", desc: "SVA & WMP, AWS Gap Assessment" },
  { title: "Udaipur Beverages", loc: "Jabalpur, MP", cat: "Audit", desc: "AWS Gap Assessment" },
  { title: "Ludhiana Beverages", loc: "Ludhiana, PB", cat: "Audit", desc: "AWS Gap Assessment (Unit 1 & 2)" },
  { title: "Male Aerated Water Co.", loc: "Maldives", cat: "Audit", desc: "SVA & WMP, AWS Gap Assessment" },
  { title: "Trishuli Water Company", loc: "Nepal", cat: "Environment", desc: "ESDD (Env & Social Due Diligence)" },
  { title: "Amrit Bottlers", loc: "Faizabad, UP", cat: "Environment", desc: "ESDD Assessment" },
  { title: "Pajson Agro (India)", loc: "Andhra Pradesh", cat: "Engineering", desc: "Repair & Upgrade of 80 KLD ETP/STP" },
  { title: "Lito Marine Products", loc: "Anakapalli, AP", cat: "Engineering", desc: "Upgrade of 100 KLD ETP" },
  { title: "MLR Agri Food Products", loc: "Andhra Pradesh", cat: "Engineering", desc: "Establishment of 50 KLD ETP/STP" },
  { title: "ITC Fortune Sreekanya", loc: "Visakhapatnam", cat: "Engineering", desc: "Supply & Installation of 45 KLD STP" },
  { title: "Ginger Hotels", loc: "Visakhapatnam", cat: "Engineering", desc: "Supply & Installation of 30 KLD STP" },
  { title: "INS Circar (Navy)", loc: "Visakhapatnam", cat: "Engineering", desc: "1.5 MLD Drain Water Treatment Plant" },
  { title: "SBKM Govt Hospital", loc: "Chhattisgarh", cat: "Engineering", desc: "Sewage Treatment Plant of 650 KLD" },
  { title: "East Point Golf Club", loc: "Visakhapatnam", cat: "Engineering", desc: "Tertiary Water System upto 2 MLD" },
  { title: "SMBS Devasthanam", loc: "Srisailam", cat: "Engineering", desc: "Turnkey 6 MLD STP based on SBR" },
  { title: "Afcons Infrastructure", loc: "J&K", cat: "Engineering", desc: "Installation of 300 KLD WWTP (Tunnel Project)" },
  { title: "Prathima Infrastructure", loc: "Telangana", cat: "Engineering", desc: "300 KLD WWTP (Lift Irrigation)" },
  { title: "Ghan Marine Products", loc: "Visakhapatnam", cat: "Engineering", desc: "Establishment of 400 KLD ETP" }
];

const getCategoryStyle = (category) => {
  switch (category) {
    case "Environment":
      return { border: "#2e7d32", bg: "#e8f5e9", text: "#1b5e20" };
    case "Audit":
      return { border: "#ed6c02", bg: "#fff3e0", text: "#e65100" };
    case "Consulting":
    case "Engineering":
    default:
      return { border: "#0288d1", bg: "#e1f5fe", text: "#01579b" };
  }
};

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false); 

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const categories = ["All", "Engineering", "Consulting", "Environment", "Audit"];

  const filtered = projectsData.filter((p) => {
    const matchCat = filter === "All" || p.cat === filter;
    const matchSearch =
      p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.loc.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  const initialLimit = 3  ;
  const displayProjects = showAll ? filtered : filtered.slice(0, initialLimit);

  return (
    <Box id="projects" sx={{ py: 8, bgcolor: "#f8f9fa" }}>
      <Container maxWidth="lg">

        <Box textAlign="center" mb={5}>
          <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>
            TRACK RECORD
          </Typography>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1, fontSize: { xs: '2rem', md: '3rem'} }}>
            Project Portfolio
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Delivering sustainable engineering solutions since 2014.
          </Typography>
        </Box>

        <Box
          sx={{
            mb: 4,
            p: 2,
            borderRadius: 3,
            bgcolor: "white",
            boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: "center"
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              overflowX: "auto",
              width: "100%",
              pb: isMobile ? 1 : 0,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: 'none'
            }}
          >
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                clickable
                onClick={() => {
                   setFilter(cat);
                   setShowAll(false); // 
                }}
                sx={{
                  fontWeight: 600,
                  bgcolor: filter === cat ? "primary.main" : "white",
                  color: filter === cat ? "#fff" : "text.primary",
                  border: "1px solid #ddd",
                  transition: '0.2s'
                }}
              />
            ))}
          </Stack>

          <TextField
            placeholder="Search clients..."
            size="small"
            fullWidth={isMobile}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="disabled" />
                </InputAdornment>
              )
            }}
            sx={{ bgcolor: "#f1f3f4", borderRadius: 2, minWidth: 250 }}
          />
        </Box>

        <Grid container spacing={3}>
          {displayProjects.map((p, i) => {
            const st = getCategoryStyle(p.cat);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Fade in={true} timeout={300 + (i * 50)}>
                  <Card
                    sx={{
                      height: '100%', 
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      borderLeft: `6px solid ${st.border}`,
                      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                      transition: "0.2s",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="h6" fontWeight={700} lineHeight={1.2} sx={{ fontSize: '1.05rem' }}>
                          {p.title}
                        </Typography>
                        <Chip
                          label={p.cat}
                          size="small"
                          sx={{
                            bgcolor: st.bg,
                            color: st.text,
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 22,
                            ml: 1
                          }}
                        />
                      </Box>

                      <Typography color="text.secondary" variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                        {p.desc}
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      <Box display="flex" alignItems="center" mt="auto">
                        <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: '#90a4ae' }} />
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {p.loc}
                        </Typography>
                        <Box ml="auto">
                          <ArrowForwardIosIcon sx={{ fontSize: 12, opacity: 0.4 }} />
                        </Box>
                      </Box>
                      
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>

        {filtered.length > initialLimit && (
           <Box textAlign="center" mt={6}>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => setShowAll(!showAll)}
                endIcon={showAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                sx={{ 
                    borderRadius: 50, 
                    px: 4, 
                    py: 1, 
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 }
                }}
              >
                {showAll ? "Show Less" : `View All Projects`}
              </Button>
           </Box>
        )}

      </Container>
    </Box>
  );
}